import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  executeGuest,
  getBookingDetail,
  fetchGuestsCosts,
  deleteGuestsCost,
  approveBooking
} from '../../api/sportaloka-client';
import { getHistoryContent, getStatusColor, getStatusLabel } from '../../helpers/status';
import { format } from 'date-fns';
import BookingHistory from './components/BookingHistory';
import Swal from 'sweetalert2';
import RejectModal from './components/RejectModal';
import RescheduleModal from './components/BookingReschedule';
import ExecutionModal from './components/ExecutionModal';
import AddCostModal from './components/AddCostModal';

const BookingDetail = () => {
  const { id } = useParams();
  const [initLoading, setInitLoading] = useState();
  const [initError, setInitError] = useState();
  const [data, setData] = useState(null);
  const [histories, setHistories] = useState([]);
  const [selectedTab, setSelectedTab] = useState('informasi-pesanan');
  const [selectedGuest, setSelectedGuest] = useState(null);

  const [fetchGuestsCostsLoading, setFetchGuestsCostsLoading] = useState(false);
  const [fetchGuestsCostsError, setFetchGuestsCostsError] = useState(false);
  const [guestsCosts, setGuestsCosts] = useState([]);

  const getData = async () => {
    try {
      setInitError(false);
      setInitLoading(true);
      const response = await getBookingDetail(id);
      const responseData = response.data.data;
      const bookingHistories = responseData.booking_histories.map((item) => {
        return getHistoryContent(item);
      });
      setData(responseData);
      setHistories(bookingHistories);
      setInitLoading(false);
    } catch (error) {
      setInitError(true);
      setInitLoading(false);
    }
  };

  const groupByGuest = (data) => {
    return data.reduce((acc, item) => {
      const { guest_id, guest, quantity, tenant_cost } = item;
      if (!acc[guest_id]) {
        acc[guest_id] = {
          id: guest_id,
          guest: guest,
          costs: [],
          total: 0
        };
      }

      const subtotal = tenant_cost.amount * quantity;

      acc[guest_id].costs.push({
        ...tenant_cost,
        quantity: quantity,
        subtotal: subtotal
      });
      acc[guest_id].total += subtotal;

      return acc;
    }, {});
  };

  const getGuestsCosts = async () => {
    try {
      setFetchGuestsCostsLoading(true);
      setFetchGuestsCostsError(false);
      const response = await fetchGuestsCosts(id);
      const responseData = groupByGuest(response.data.data);
      setGuestsCosts(Object.values(responseData));
      setFetchGuestsCostsLoading(false);
    } catch (error) {
      setFetchGuestsCostsLoading(false);
      setFetchGuestsCostsError(true);
    }
  };

  useEffect(() => {
    getData();
    getGuestsCosts();
  }, []);

  const handleResetData = async () => {
    await getData();
    await getGuestsCosts();
  };

  const handleExecution = (id, fullname) => {
    Swal.fire({
      title: `Apakah tamu ${fullname} sudah terlaksana?`,
      text: 'Aksi ini tidak dapat dikembalikan',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ya',
      cancelButtonText: 'Tidak'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setInitLoading(true);
          await executeGuest(id);
          await getData();
          Swal.fire({
            title: 'Berhasil!',
            text: `Pesanan telah disetujui`,
            icon: 'success',
            confirmButtonText: 'Tutup'
          });
          setInitLoading(false);
        } catch (error) {
          Swal.fire({
            title: 'Gagal!',
            text: `Periksa koneksi internet anda`,
            icon: 'error',
            confirmButtonText: 'Tutup'
          });
          setInitLoading(false);
        }
      }
    });
  };

  const handleApprove = () => {
    Swal.fire({
      title: 'Apakah anda yakin ingin menyetujui pesanan ini?',
      text: 'Aksi ini tidak dapat dikembalikan',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ya',
      cancelButtonText: 'Tidak'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setInitLoading(true);
          await approveBooking(id);
          await getData();
          Swal.fire({
            title: 'Berhasil!',
            text: `Pesanan telah disetujui`,
            icon: 'success',
            confirmButtonText: 'Tutup'
          });
          setInitLoading(false);
        } catch (error) {
          Swal.fire({
            title: 'Gagal!',
            text: `Periksa koneksi internet anda`,
            icon: 'error',
            confirmButtonText: 'Tutup'
          });
          setInitLoading(false);
        }
      }
    });
  };

  const handleDeleteGuestsCosts = (id) => {
    Swal.fire({
      title: 'Apakah anda yakin ingin menghapus rincian biaya tamu?',
      text: 'Aksi ini tidak dapat dikembalikan',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ya',
      cancelButtonText: 'Tidak'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setInitLoading(true);
          await deleteGuestsCost(id);
          handleResetData();
          Swal.fire({
            title: 'Berhasil!',
            text: `Data berhasil dihapus`,
            icon: 'success',
            confirmButtonText: 'Tutup'
          });
          setInitLoading(false);
        } catch (error) {
          Swal.fire({
            title: 'Gagal!',
            text: `Periksa koneksi internet anda`,
            icon: 'error',
            confirmButtonText: 'Tutup'
          });
          setInitLoading(false);
        }
      }
    });
  };

  return (
    <div className="row y-gap-20 justify-between items-end pb-60 lg:pb-40 md:pb-32">
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="text-30 lh-14 fw-600">Detail Pesanan</h1>
          <div className="d-flex">
            <Link to="/bookings" className="button -sm px-3 bg-white h-50 text-dark mr-10">
              <i className="fa fa-arrow-left mr-10" />
              Kembali
            </Link>

            <div className="d-flex">
              {data && data.status === 'NEW' && (
                <button
                  disabled={initLoading}
                  onClick={handleApprove}
                  className="button -sm px-3 bg-blue-1 h-50 text-white mr-10">
                  <i className="fa fa-check text-16 mr-10" />
                  Setujui
                </button>
              )}
              {data &&
                (data.status === 'NEW' ||
                  data.status === 'RESCHEDULE' ||
                  data.status === 'APPROVED') && (
                  <button
                    data-bs-toggle="modal"
                    data-bs-target="#rejectBookingModal"
                    disabled={initLoading}
                    className="button -sm px-3 bg-danger h-50 text-white mr-10">
                    <i className="fa fa-close text-16 mr-10" />
                    Tolak
                  </button>
                )}

              {data && data.status === 'NEW' && (
                <button
                  data-bs-toggle="modal"
                  data-bs-target="#rescheduleBookingModal"
                  disabled={initLoading}
                  className="button -sm px-3 bg-warning h-50 text-dark fw-600">
                  <i className="fa fa-calendar text-16 mr-10" />
                  Reschedule
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-3">
            <BookingHistory
              data={histories}
              initLoading={initLoading}
              initError={initError}
              retry={getData}
            />
          </div>
          <div className="col-9">
            <div
              className="py-30 px-30 rounded-4 bg-white shadow-3 mt-20"
              style={{ minHeight: 540 }}>
              {initLoading && !initError && (
                <div
                  className="d-flex align-items-center justify-content-center h-100"
                  style={{ minHeight: 540 }}>
                  <div className="loader" />
                  <div className="fw-600" style={{ marginLeft: 12 }}>
                    Loading...
                  </div>
                </div>
              )}
              {!initLoading && initError && (
                <div
                  className="d-flex align-items-center justify-content-center h-100 text-center"
                  style={{ minHeight: 540 }}>
                  <p className="fw-600">
                    Gagal! Periksa koneksi internet anda dan{' '}
                    <span className="text-danger cursor-pointer" onClick={getData}>
                      Coba lagi
                    </span>
                  </p>
                </div>
              )}
              {!initLoading && !initError && (
                <div>
                  <div className="custom-tabs">
                    <div
                      className={selectedTab === 'informasi-pesanan' ? 'active' : ''}
                      onClick={() => setSelectedTab('informasi-pesanan')}>
                      Informasi Pemesanan
                    </div>
                    {data &&
                      (data.status === 'PAID' ||
                        data.status === 'DONE' ||
                        data.status === 'EXECUTION') && (
                        <div
                          className={selectedTab === 'pelaksanaan-biaya' ? 'active' : ''}
                          onClick={() => setSelectedTab('pelaksanaan-biaya')}>
                          Pelaksanaan dan Biaya
                        </div>
                      )}
                  </div>
                  {selectedTab === 'informasi-pesanan' && (
                    <div>
                      <div>
                        <h5 className="fw-700 text-blue-1 text-18">Detail Pesanan</h5>
                        <table className="table table-striped mt-20">
                          <tbody>
                            <tr>
                              <td className="px-3 py-2" width="30%">
                                Kode Booking
                              </td>
                              <td className="px-3 py-2" width="5%">
                                :
                              </td>
                              <th className="text-right px-3 py-2 text-14">
                                {data && data.booking_code}
                              </th>
                            </tr>
                            <tr>
                              <td className="px-3 py-2" width="30%">
                                Nama Paket
                              </td>
                              <td className="px-3 py-2" width="5%">
                                :
                              </td>
                              <th className="text-right px-3 py-2 text-14">
                                {data && data.package.name_id}
                              </th>
                            </tr>
                            <tr>
                              <td className="px-3 py-2" width="30%">
                                Tanggal Pemesanan
                              </td>
                              <td className="px-3 py-2" width="5%">
                                :
                              </td>
                              <th className="text-right px-3 py-2 text-14">
                                {data && format(data.booked_on_date, 'dd/MM/yyyy hh:mm')}
                              </th>
                            </tr>
                            <tr>
                              <td className="px-3 py-2" width="30%">
                                Tanggal Pelaksanaan
                              </td>
                              <td className="px-3 py-2" width="5%">
                                :
                              </td>
                              <th className="text-right px-3 py-2 text-14">
                                {data && format(data.booked_for_date, 'dd/MM/yyyy')}
                              </th>
                            </tr>
                            {data && data.status === 'RESCHEDULED' && (
                              <tr>
                                <td className="px-3 py-2" width="30%">
                                  Tanggal Penjadwalan Ulang
                                </td>
                                <td className="px-3 py-2" width="5%">
                                  :
                                </td>
                                <th className="text-right px-3 py-2 text-14">
                                  {data && format(data.reschedule_date, 'dd/MM/yyyy')}
                                </th>
                              </tr>
                            )}
                            <tr>
                              <td className="px-3 py-2" width="30%">
                                Status Pesanan
                              </td>
                              <td className="px-3 py-2" width="5%">
                                :
                              </td>
                              <th className="text-right px-3 py-2 text-14">
                                <span
                                  className={`px-2 py-1 rounded-1 fw-600 ${data && getStatusColor(data.status)}`}>
                                  {data && getStatusLabel(data.status)}
                                </span>
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-50">
                        <h5 className="fw-700 text-blue-1 text-18">Detail Pemesan</h5>
                        <table className="table table-striped mt-20">
                          <tbody>
                            <tr>
                              <td className="px-3 py-2" width="30%">
                                Nama Pemesan
                              </td>
                              <td className="px-3 py-2" width="5%">
                                :
                              </td>
                              <th className="text-right px-3 py-2 text-14">
                                {data && data.user.fullname}
                              </th>
                            </tr>
                            <tr>
                              <td className="px-3 py-2" width="30%">
                                Email Pemesan
                              </td>
                              <td className="px-3 py-2" width="5%">
                                :
                              </td>
                              <th className="text-right px-3 py-2 text-14">
                                {data && data.user.email}
                              </th>
                            </tr>
                            <tr>
                              <td className="px-3 py-2" width="30%">
                                Telepon Pemesan
                              </td>
                              <td className="px-3 py-2" width="5%">
                                :
                              </td>
                              <th className="text-right px-3 py-2 text-14">
                                {data && data.user.phone_number}
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-50">
                        <h5 className="fw-700 text-blue-1 text-18">Detail Tamu</h5>
                        <table className="table table-striped mt-20">
                          <thead>
                            <tr>
                              <th className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0">
                                Nama Lengkap
                              </th>
                              <th className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0">
                                Jenis Tamu
                              </th>
                              <th className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0">
                                Umur (Tahun)
                              </th>
                              <th className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0">
                                Berat (Kg)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {data &&
                              data.guests.map((guest) => {
                                return (
                                  <tr key={guest.id}>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0">
                                      {guest.fullname}
                                    </td>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0">
                                      {guest.type === 'LOCAL' ? 'Lokal' : 'Wisman'}
                                    </td>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0">
                                      {guest.age}
                                    </td>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0">
                                      {guest.weight}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {selectedTab === 'pelaksanaan-biaya' && (
                    <div>
                      <div>
                        <h5 className="fw-700 text-blue-1 text-18">Pelaksanaan Tamu</h5>
                        <table className="table table-striped mt-20">
                          <thead>
                            <tr>
                              <th
                                width="30%"
                                className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0">
                                Nama Lengkap
                              </th>
                              <th className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0">
                                Jenis Tamu
                              </th>
                              <th className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0">
                                Status
                              </th>
                              <th className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0">
                                Tanggal Reschedule
                              </th>
                              <th className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0 text-center">
                                Aksi
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {data &&
                              data.guests.map((guest) => {
                                return (
                                  <tr key={guest.id}>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0">
                                      {guest.fullname}
                                    </td>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0">
                                      {guest.type === 'LOCAL' ? 'Lokal' : 'Wisman'}
                                    </td>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0">
                                      {guest.guest_costs.length > 0 ? 'DONE' : guest.status}
                                    </td>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0">
                                      {guest.rescheduled_at
                                        ? format(guest.rescheduled_at, 'dd/MM/yyyy')
                                        : '-'}
                                    </td>
                                    <td className="px-3 py-3 text-14 fw-700 rounded-0 text-center">
                                      {guest.status !== 'EXCECUTED' && data.status !== 'DONE' && (
                                        <button
                                          onClick={() => handleExecution(guest.id, guest.fullname)}
                                          type="button"
                                          title="Terlaksana"
                                          className="btn btn-sm text-dark fw-600 me-3 px-0 py-0">
                                          <i className="text-success fa fa-check"></i> Terlaksana
                                        </button>
                                      )}
                                      {guest.status !== 'EXCECUTED' && data.status !== 'DONE' && (
                                        <button
                                          onClick={() => setSelectedGuest(guest.id)}
                                          data-bs-toggle="modal"
                                          data-bs-target="#executionModal"
                                          type="button"
                                          title="Jadwalkan Pelaksanaan Berikutnya"
                                          className="btn btn-sm text-dark fw-600 px-0 py-0">
                                          <i className="text-primary fa fa-calendar"></i> Reschedule
                                        </button>
                                      )}

                                      {(guest.status === 'EXCECUTED' &&
                                        guest.guest_costs.length) === 0 && (
                                        <button
                                          type="button"
                                          data-bs-toggle="modal"
                                          data-bs-target="#addCostModal"
                                          onClick={() => setSelectedGuest(guest.id)}
                                          className="btn btn-warning btn-sm py-0 rounded px-2 fw-500">
                                          <i className="fa fa-plus" /> Biaya
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                        <ExecutionModal resetData={handleResetData} guestId={selectedGuest} />
                        <AddCostModal resetData={handleResetData} guestId={selectedGuest} />
                      </div>
                      <div className="mt-20">
                        <h5 className="fw-700 text-blue-1 text-18">Rincian Biaya</h5>
                        <table className="table table-border mt-20">
                          <thead>
                            <tr>
                              <th
                                className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0"
                                width="25%">
                                Nama tamu
                              </th>
                              <th
                                className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0"
                                width="25%">
                                Item
                              </th>
                              <th
                                className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0 text-right"
                                width="20%">
                                Item Price
                              </th>
                              <th
                                className="px-3 py-3 text-14 fw-700 bg-blue-1 text-white rounded-0 text-right"
                                width="20%">
                                Subtotal
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {fetchGuestsCostsLoading && !fetchGuestsCostsError && (
                              <tr>
                                <th className="py-3 px-3 text-14 text-center" colSpan={5}>
                                  Loading...
                                </th>
                              </tr>
                            )}
                            {!fetchGuestsCostsLoading && fetchGuestsCostsError && (
                              <tr>
                                <th className="py-3 px-3 text-14 text-center" colSpan={5}>
                                  Gagal mendapatkan data{' '}
                                  <span
                                    className="cursor-pointer text-danger"
                                    onClick={handleResetData}>
                                    Coba lagi
                                  </span>
                                </th>
                              </tr>
                            )}
                            {!fetchGuestsCostsLoading &&
                              !fetchGuestsCostsError &&
                              guestsCosts.length === 0 && (
                                <tr>
                                  <th className="py-3 px-3 text-14 text-center" colSpan={5}>
                                    Data Tidak Ditemukan
                                  </th>
                                </tr>
                              )}

                            {!fetchGuestsCostsLoading &&
                              !fetchGuestsCostsError &&
                              guestsCosts.length > 0 &&
                              guestsCosts.map((guestCost) => (
                                <React.Fragment key={guestCost.id}>
                                  {/* Guest Name Row */}
                                  <tr>
                                    <td colSpan={5} className="px-3 py-2 text-14 fw-700 rounded-0">
                                      {guestCost.guest.fullname}
                                    </td>
                                  </tr>

                                  {/* Costs for each guest */}
                                  {guestCost.costs.map((cost) => (
                                    <tr key={cost.id}>
                                      <td></td> {/* Empty cell for the guest name */}
                                      <td className="px-3 py-2 text-14">{cost.description}</td>
                                      <td className="px-3 py-2 text-14 text-right">
                                        IDR {cost.amount.toLocaleString()}
                                      </td>
                                      <td className="px-3 py-2 text-14 text-right">
                                        IDR {cost.subtotal.toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}

                                  {/* Total Row for each guest */}
                                  <tr>
                                    <td
                                      colSpan={3}
                                      className="px-3 py-2 text-14 fw-700 text-right bg-blue-2">
                                      Total
                                    </td>
                                    <td
                                      colSpan={1}
                                      className="px-3 py-2 text-14 fw-700 text-right bg-blue-2">
                                      IDR {guestCost.total.toLocaleString()}
                                    </td>
                                  </tr>
                                </React.Fragment>
                              ))}
                            {!fetchGuestsCostsLoading &&
                              !fetchGuestsCostsError &&
                              guestsCosts.length > 0 && (
                                <tr>
                                  <td className="px-3 py-2 bg-blue-1 text-white fw-700" colSpan={2}>
                                    TOTAL BIAYA KESELURUHAN
                                  </td>
                                  <td
                                    className="px-3 py-2 bg-blue-1 text-white fw-700 text-right"
                                    colSpan={2}>
                                    IDR{' '}
                                    {guestsCosts
                                      .reduce((acc, curr) => {
                                        return acc + curr.total;
                                      }, 0)
                                      .toLocaleString()}
                                    {/* {guestsCosts.length > 0 &&
                                    guestsCosts.reduce((acc, item) => {
                                      return acc + item.total;
                                    }, 0)()} */}
                                  </td>
                                </tr>
                              )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <RejectModal resetData={handleResetData} />
        <RescheduleModal resetData={handleResetData} />
      </div>
    </div>
  );
};

export default BookingDetail;
